import {
  campaignResponse,
  countryEnum,
  CreativeInput,
  CreativeMetricsInput,
  DashboardTypes,
  datatype,
  filtertype,
  FlowchartCampaignStatus,
  GetPublishedOutputQuery,
  GetQueriesQuery,
  Placeholder,
  PlaceholderObject,
  publishedType,
  PublishOutputLocation_v2Mutation,
  PublishOutputLocationMutation,
  queryInput,
  UnPublishCampaignMutation,
  UnPublishMutation,
} from 'API';
import { API } from 'aws-amplify';
import { publishOutputLocation_v2, unPublish, unPublishCampaign } from 'graphql/mutations';
import { getPublishedOutput, getQueries, getStoredQuery } from 'graphql/queries';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import APIContext from './APIContext';
import { ACdetails, CampaignContext, useCampaignContext } from './CampaignContext';
import { useDataQuery } from 'customHooks/useDataQuery';
import { useUpdateCampaignStatus } from 'components/NewPortal/Main/Brief/CustomHook/useUpdateCampaignStatus';
import { checkIfPublished } from '../components/NewPortal/Main/Summary/ReportsLinks';
import { useTemplateContext } from './TemplateContext';
import { useFetchPlanData } from 'components/NewPortal/Main/Plan/useFetchPlanData';
import { useClientContext } from './ClientContext';
import { useCheckRollbackStatus } from 'customHooks/useCheckRollbackStatus';
import { set } from 'lodash';
import InputContext from './InputContext';
import { useVideoParams } from 'customHooks/useParameters';

export const PublishContext = createContext({
  publishCampaign: () => {},
  unpublishCampaign: () => {},
  updateOLArray: (args: any) => {},
  setPublishedData: (args: any) => {},
  publishedData: [] as any[],
  isPublished: false,
  handlePublishSummary: () => {},
  campaignOverFlowqueryStatus: 'NOT STARTED' as 'RUNNING' | 'FAILED' | 'DONE' | 'NOT STARTED',
  removePlaceholderfromPublishArray: (args: any) => {},
  placeholders: {
    DIGITAL: [] as any[],
    TV: [] as any[],
    RADIO: [] as any[],
    OOH: [] as any[],
    PRINT: [] as any[],
    CINEMA: [] as any[],
    SEARCH: [] as any[],
  },
  placeholdersStatus: [] as placeholdersStatusType[],
  setPlaceholdersStatus: (args: placeholdersStatusType[]) => {},
  placeholderResetToken: 0,
});

export type placeholdersStatusType = {
  id: string;
  loaded: boolean;
};
export const PublishContextProvider = (props: any) => {
  const { activeCampaign, checkDashPublished, dashType, activeSubCampaign, overflowKPIquery } = useCampaignContext();
  const { activeOrganization, userCanDoInsource } = useClientContext();
  const { storedQueriesOnCurrentDash, setStoredQueriesOnCurrentDash, creativeLibrarySections } =
    useContext(InputContext);
  const { getEnvironment } = useContext(APIContext);
  const [publishedData, setPublishedData] = useState<Placeholder[]>([]);
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const [campaignOverflowKPIquery, setCampaignOverflowKPIquery] = useState<queryInput>();
  const [videoSource] = useVideoParams();
  const [placeholders, setPlaceholders] = useState({
    DIGITAL: [] as any[],
    TV: [] as any[],
    RADIO: [] as any[],
    OOH: [] as any[],
    PRINT: [] as any[],
    CINEMA: [] as any[],
    SUMMARY: [] as any[],
    BVOD: [] as any[],
    SEARCH: [] as any[],
  });
  const { fetchedPlaceholders } = useTemplateContext();
  const [UpdateCampaign] = useUpdateCampaignStatus();
  const { setPermanentCampaignList, permanentCampaignList } = useCampaignContext();
  const [rollbackStatus] = useCheckRollbackStatus(activeCampaign);
  const [placeholdersStatus, setPlaceholdersStatus] = useState<placeholdersStatusType[]>([]);
  const [placeholderResetToken, setPlaceholderResetToken] = useState(0);
  // console.log("placeholders", placeholders);
  let activeSubCampaignObject:
    | {
        campaignId: string;
        subCampaignId: string;
        published: publishedType;
        __typename: string;
      }
    | undefined;

  if (activeSubCampaign) {
    const subcampaignId =
      videoSource === 'BVOD' && dashType === DashboardTypes.TV ? -1 : (activeSubCampaign.subcampaignId as number);
    activeSubCampaignObject = activeCampaign?.subcampaign[subcampaignId];
  }

  useEffect(() => {
    if (activeSubCampaignObject) {
      const effectiveDash: DashboardTypes | null =
        dashType === 'INSOURCE_DATA'
          ? DashboardTypes.DIGITAL
          : videoSource === 'BVOD' && dashType === DashboardTypes.TV
            ? DashboardTypes.BVOD
            : dashType;

      const hasCampaignPeriods = (activeCampaign?.campaignPeriods?.length ?? 0) > 0;
      // When a period is active, checkCampaignPeriods spreads the parent's campaignPeriods onto
      // the activeCampaign, so hasCampaignPeriods is still true even when viewing a period.
      // Detect this by checking whether the active campaignId matches one of those period IDs.
      const isViewingPeriod =
        activeCampaign?.campaignPeriods?.some((p) => p?.CustomReportID === activeCampaign?.campaignId) ?? false;

      if (hasCampaignPeriods && !isViewingPeriod) {
        // subcampaign[-1].published may have been inflated by period propagation for visibility
        // purposes. Only trust direct subcampaign entries (key !== -1) for the publish button.
        // However, if there are NO numeric subcampaign keys at all (only -1 exists), the campaign
        // was published directly via -1, so fall back to subcampaign[-1].
        const directKeys = Object.keys(activeCampaign!.subcampaign).filter((k) => k !== '-1');
        const isDirectlyPublished =
          directKeys.length > 0
            ? directKeys.some((k) => (activeCampaign!.subcampaign as any)[k]?.published?.[effectiveDash as DashboardTypes] === true)
            : // No numeric subcampaign keys — prefer rawPublished (pre-propagation backend value)
              // to avoid reading an inflated subcampaign[-1] set by period propagation.
              // Fall back to published when rawPublished is absent (standalone campaigns that
              // were published before periods existed never get rawPublished written to them).
              ((activeCampaign!.subcampaign[-1] as any)?.rawPublished ?? (activeCampaign!.subcampaign[-1] as any)?.published)?.[effectiveDash as DashboardTypes] === true;
        dashType !== null && setIsPublished(isDirectlyPublished);
      } else {
        dashType !== null && setIsPublished(checkDashPublished(activeSubCampaignObject, effectiveDash as DashboardTypes));
      }
    } else {
      setIsPublished(false);
    }
  }, [activeSubCampaignObject, dashType, activeCampaign, videoSource]);

  const removePlaceholderfromPublishArray = (position: string) => {
    const objcopy = placeholders;
    if (position.includes('TV')) {
      objcopy.TV = objcopy.TV.filter((e) => e.position !== position);
    } else if (position.includes('BVOD')) {
      objcopy.BVOD = objcopy.BVOD.filter((e) => e.position !== position);
    } else if (position.includes('Radio')) {
      objcopy.RADIO = objcopy.RADIO.filter((e) => e.position !== position);
    } else if (position.includes('Print')) {
      objcopy.PRINT = objcopy.PRINT.filter((e) => e.position !== position);
    } else if (position.includes('OOH')) {
      objcopy.OOH = objcopy.OOH.filter((e) => e.position !== position);
    } else if (position.includes('CINEMA')) {
      objcopy.CINEMA = objcopy.CINEMA.filter((e) => e.position !== position);
    } else if (position.includes('Search')) {
      objcopy.SEARCH = objcopy.SEARCH.filter((e) => e.position !== position);
    } else {
      objcopy.DIGITAL = objcopy.DIGITAL.filter((e) => e.position !== position);
    }
    setPlaceholders(objcopy);
  };
  //TODO I have added useCallback to this function
  const updateOLArray = useCallback((locationObject: any) => {
    setPlaceholders((prev) => {
      const next = { ...prev };
      if (locationObject.position.includes('TV')) {
        if (prev.TV.some((e) => e.position === locationObject.position)) {
          next.TV = prev.TV.map((e: any) => (e.position === locationObject.position ? locationObject : e));
        } else {
          next.TV = [...prev.TV, locationObject];
        }
      } else if (locationObject.position.includes('BVOD')) {
        if (prev.BVOD.some((e) => e.position === locationObject.position)) {
          next.BVOD = prev.BVOD.map((e: any) => (e.position === locationObject.position ? locationObject : e));
        } else {
          next.BVOD = [...prev.BVOD, locationObject];
        }
      } else if (locationObject.position.includes('Radio')) {
        if (prev.RADIO.some((e) => e.position === locationObject.position)) {
          next.RADIO = prev.RADIO.map((e: any) => (e.position === locationObject.position ? locationObject : e));
        } else {
          next.RADIO = [...prev.RADIO, locationObject];
        }
      } else if (locationObject.position.includes('Print')) {
        if (prev.PRINT.some((e) => e.position === locationObject.position)) {
          next.PRINT = prev.PRINT.map((e: any) => (e.position === locationObject.position ? locationObject : e));
        } else {
          next.PRINT = [...prev.PRINT, locationObject];
        }
      } else if (locationObject.position.includes('OOH')) {
        if (prev.OOH.some((e) => e.position === locationObject.position)) {
          next.OOH = prev.OOH.map((e: any) => (e.position === locationObject.position ? locationObject : e));
        } else {
          next.OOH = [...prev.OOH, locationObject];
        }
      } else if (locationObject.position.includes('Cinema')) {
        if (prev.CINEMA.some((e) => e.position === locationObject.position)) {
          next.CINEMA = prev.CINEMA.map((e: any) => (e.position === locationObject.position ? locationObject : e));
        } else {
          next.CINEMA = [...prev.CINEMA, locationObject];
        }
      } else if (locationObject.position.includes('VALUE')) {
        if (prev.SUMMARY.some((e) => e.position === locationObject.position)) {
          next.SUMMARY = prev.SUMMARY.map((e: any) => (e.position === locationObject.position ? locationObject : e));
        } else {
          next.SUMMARY = [...prev.SUMMARY, locationObject];
        }
      } else if (locationObject.position.includes('Search')) {
        if (prev.SEARCH.some((e) => e.position === locationObject.position)) {
          next.SEARCH = prev.SEARCH.map((e: any) => (e.position === locationObject.position ? locationObject : e));
        } else {
          next.SEARCH = [...prev.SEARCH, locationObject];
        }
      } else {
        if (prev.DIGITAL.some((e) => e.position === locationObject.position)) {
          next.DIGITAL = prev.DIGITAL.map((e: any) => {
            if (e.position === locationObject.position) {
              const sanitizedMetrics = Array.isArray(locationObject.metrics)
                ? locationObject.metrics.map((o: any) => {
                    delete o.__typename;
                    return o;
                  })
                : locationObject.metrics;
              return { ...locationObject, metrics: sanitizedMetrics };
            }
            return e;
          });
        } else {
          const processedObject = { ...locationObject };
          if (Array.isArray(processedObject.metrics)) {
            processedObject.metrics = processedObject.metrics.map((o: any) => {
              delete o.__typename;
              return o;
            });
          }
          next.DIGITAL = [...prev.DIGITAL, processedObject];
        }
      }
      return next;
    });
  }, []);

  const publishCampaign = useCallback(
    async (summaryPlaceholders?: PlaceholderObject[]) => {
      // console.log('PB', placeholders);
      console.log('PB', videoSource, {
        campaignId: activeCampaign?.campaignId?.toString(),
        placeholders: summaryPlaceholders
          ? summaryPlaceholders
          : placeholders[
              dashType === 'INSOURCE_DATA'
                ? 'DIGITAL'
                : dashType === 'TV' && videoSource === 'BVOD'
                  ? 'BVOD'
                  : (dashType as keyof typeof placeholders)
            ],
        dashboardType:
          DashboardTypes[
            dashType === 'INSOURCE_DATA'
              ? 'DIGITAL'
              : dashType === 'TV' && videoSource === 'BVOD'
                ? 'BVOD'
                : (dashType as keyof typeof DashboardTypes)
          ],
        clientCode: activeCampaign?.ClientCode,
        clientCountry: activeCampaign?.ClientCountry,
        subCampaignId:
          dashType === 'TV' && videoSource === 'BVOD' ? '-1' : activeSubCampaign?.subcampaignId?.toString(),
      });

      try {
        const placeholdersToPublish = summaryPlaceholders
          ? summaryPlaceholders
          : (() => {
              const baseKey =
                dashType === 'INSOURCE_DATA'
                  ? 'DIGITAL'
                  : dashType === 'TV' && videoSource === 'BVOD'
                    ? 'BVOD'
                    : (dashType as keyof typeof placeholders);

              const basePlaceholders = placeholders[baseKey];

              if (dashType === 'INSOURCE_DATA' || dashType === 'DIGITAL') {
                const turnCreativeLibrarySectionsIntoPlaceholders = creativeLibrarySections.map((section) => ({
                  position: section.position,
                  outputlocation: section.outputLocation,
                  creatives: section.creatives.map((creative) => {
                    return {
                      id: creative.id,
                      position: creative.position,
                      creativeOutputLocation: creative.file,
                      creativeID: creative.creativeID,
                      creativeName: creative.creativeName,
                      metrics: creative.metrics.map((metric) => {
                        return {
                          position: metric.position,
                          label: metric.label,
                          value: metric.value,
                          ...(metric.column && { column: metric.column }),
                          ...(metric.decimals && { decimals: metric.decimals }),
                        } as CreativeMetricsInput;
                      }),
                    } as CreativeInput;
                  }) as CreativeInput[],
                  title: section.title,
                })) as PlaceholderObject[];
                // console.log('CPL', turnCreativeLibrarySectionsIntoPlaceholders, creativeLibrarySections);

                return [...basePlaceholders, ...turnCreativeLibrarySectionsIntoPlaceholders];
              }

              return basePlaceholders;
            })();

        const publish = (await API.graphql({
          query: publishOutputLocation_v2,
          variables: {
            publishInput: {
              campaignId: activeCampaign?.campaignId?.toString(),
              placeholders: placeholdersToPublish,
              dashboardType:
                DashboardTypes[
                  dashType === 'INSOURCE_DATA'
                    ? 'DIGITAL'
                    : dashType === 'TV' && videoSource === 'BVOD'
                      ? 'BVOD'
                      : (dashType as keyof typeof DashboardTypes)
                ],
              clientCode: activeCampaign?.ClientCode,
              clientCountry: activeCampaign?.ClientCountry,
              subCampaignId:
                dashType === 'TV' && videoSource === 'BVOD' ? '-1' : activeSubCampaign?.subcampaignId?.toString(),
            },
            Environment: getEnvironment,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: PublishOutputLocation_v2Mutation };
        if (publish.data.publishOutputLocation_v2?.startsWith('Successfully published')) {
          setIsPublished(true);
          if (activeCampaign?.status !== FlowchartCampaignStatus.CAMPAIGN_PARTLY_PUBLISHED) {
            if (dashType !== DashboardTypes.FLOWCHART) {
              UpdateCampaign(activeCampaign, FlowchartCampaignStatus.CAMPAIGN_PARTLY_PUBLISHED);
              window.location.reload();
            } else {
              setCampaignOverflowKPIquery(undefined);
            }
          } else {
            if (dashType !== DashboardTypes.FLOWCHART) {
              window.location.reload();
            }
          }
        }
      } catch (e) {
        console.log('NOT PUBLISHED BCZ', e);
      }
    },
    [
      activeCampaign,
      activeSubCampaign,
      dashType,
      getEnvironment,
      placeholders,
      storedQueriesOnCurrentDash,
      videoSource,
      creativeLibrarySections,
    ],
  );

  async function unpublishCampaign() {
    try {
      // eslint - disable - next - line @typescript-eslint / no - unused - vars
      const unpublish = (await API.graphql({
        query: unPublishCampaign,
        variables: {
          campaign_id: activeCampaign?.campaignId?.toString(),
          clientCode: activeCampaign?.ClientCode,
          clientCountry: activeCampaign?.ClientCountry,
          dashboardType:
            DashboardTypes[
              dashType === 'INSOURCE_DATA'
                ? 'DIGITAL'
                : dashType === 'TV' && videoSource === 'BVOD'
                  ? 'BVOD'
                  : (dashType as keyof typeof DashboardTypes)
            ],
          subCampaignId:
            dashType === 'TV' && videoSource === 'BVOD' ? '-1' : activeSubCampaign?.subcampaignId?.toString(),
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: UnPublishCampaignMutation };

      if (unpublish.data.unPublishCampaign?.StatusCode === 200) {
        setIsPublished(false);

        if (activeCampaign?.status === FlowchartCampaignStatus.CAMPAIGN_PARTLY_PUBLISHED) {
          const anyPublished = () => {
            const arr: any = [];
            if (activeCampaign?.subcampaign) {
              for (const [key, value] of Object.entries(activeCampaign.subcampaign)) {
                const publishedobj = (value as any).published;
                //check all propertues of publishedobj and if any is true return true
                const isPublished = Object.values(publishedobj).filter((e) => e === true);
                arr.push(...isPublished);
              }
            }
            return arr.length > 1;
          };

          const q = anyPublished();

          if (!q) {
            const rollbackTo = rollbackStatus?.hasPlan
              ? 'PLANNING_DONE'
              : !rollbackStatus?.hasPlan && rollbackStatus?.hasBrief
                ? 'BRIEF_COMPLETED'
                : 'CREATED';
            UpdateCampaign(activeCampaign, FlowchartCampaignStatus[rollbackTo]);
            setTimeout(() => {
              // window.location.reload();
            }, 1000);
            // const campaign = campaignList?.find((campaign: any) => campaign.campaignId === activeCampaign?.campaignId);
            // const newCampaign = {
            //   ...campaign,
            //   status: rollbackTo,
            //   subcampaign: { ...campaign?.subcampaign, [activeSubCampaign?.subcampaignId as number]: { ...campaign?.subcampaign[activeSubCampaign?.subcampaignId as number], published: { ...campaign?.subcampaign[activeSubCampaign?.subcampaignId as number].published, [dashType]: false } } }
            // } as ACdetails;

            // const newCampaignList = campaignList?.map((campaign: ACdetails) => {
            //   if (campaign?.campaignId === activeCampaign?.campaignId) {
            //     return newCampaign;
            //   } else {
            //     return campaign;
            //   }
            // }) as ACdetails[];
            // setPermanentCampaignList(newCampaignList);
          } else {
            // window.location.reload();
          }
        } else if (activeCampaign?.status === FlowchartCampaignStatus.CAMPAIGN_PUBLISHED) {
          if (dashType === DashboardTypes.FLOWCHART) {
            // const rollbackTo = rollbackStatus?.hasPlan ? "PLANNING_DONE" : !rollbackStatus?.hasPlan && rollbackStatus?.hasBrief ? "BRIEF_COMPLETED" : "CREATED"
            UpdateCampaign(activeCampaign, FlowchartCampaignStatus.CAMPAIGN_PARTLY_PUBLISHED);
            setTimeout(() => {
              // window.location.reload();
            }, 1000);
            // const campaign = campaignList?.find((campaign: any) => campaign.campaignId === activeCampaign?.campaignId);
            // const newCampaign = {
            //   ...campaign,
            //   status: rollbackTo,
            //   subcampaign: { ...campaign?.subcampaign, [activeSubCampaign?.subcampaignId as number]: { ...campaign?.subcampaign[activeSubCampaign?.subcampaignId as number], published: { ...campaign?.subcampaign[activeSubCampaign?.subcampaignId as number].published, [dashType]: false } } }
            // } as ACdetails;

            // const newCampaignList = campaignList?.map((campaign: ACdetails) => {
            //   if (campaign?.campaignId === activeCampaign?.campaignId) {
            //     return newCampaign;
            //   } else {
            //     return campaign;
            //   }
            // }) as ACdetails[];
            // setPermanentCampaignList(newCampaignList);
          }
        }
      } else {
        console.log('NOT UNPUBLISHED BCZ', unpublish.data.unPublishCampaign?.StatusCode);
      }
    } catch (e) {
      console.log('NOT UNPUBLISHED BCZ', e);
    }
  }

  useEffect(() => {
    setPlaceholders({
      DIGITAL: [] as any[],
      TV: [] as any[],
      RADIO: [] as any[],
      OOH: [] as any[],
      PRINT: [] as any[],
      CINEMA: [] as any[],
      SUMMARY: [] as any[],
      BVOD: [] as any[],
      SEARCH: [] as any[],
    });
    setPlaceholdersStatus([]);
    setPlaceholderResetToken((t) => t + 1);
  }, [activeCampaign, dashType, fetchedPlaceholders, activeSubCampaign]);

  useEffect(() => {
    const checkDashPublished = (
      subCampaignO:
        | {
            campaignId: string;
            subCampaignId: string;
            published: publishedType;
            __typename: string;
          }
        | undefined,
      dash: DashboardTypes,
    ) => {
      if (subCampaignO === undefined) return false;
      if (dash === 'DIGITAL') {
        return subCampaignO?.published.DIGITAL === true;
      }
      if (dash === 'TV') {
        return subCampaignO?.published.TV === true;
      }
      if (dash === 'RADIO') {
        return subCampaignO?.published.RADIO === true;
      }
      if (dash === 'PRINT') {
        return subCampaignO?.published.PRINT === true;
      }
      if (dash === 'OOH') {
        return subCampaignO?.published.OOH === true;
      }
      if (dash === 'CINEMA') {
        return subCampaignO?.published.CINEMA === true;
      }
      if (dash === 'BVOD') {
        return subCampaignO?.published.BVOD === true;
      }
      if (dash === 'SEARCH') {
        return subCampaignO?.published.SEARCH === true;
      }
      return false;
    };
    // console.log(activeCampaign?.ClientCode, activeCampaign?.ClientCountry, activeCampaign?.authorized, activeCampaign?.campaignId, activeSubCampaign?.subcampaignId, activeSubCampaignObject, checkDashPublished, dashType)
    let cancelled = false;

    const fetch = async () => {
      try {
        console.count('fetch');
        const response = (await API.graphql({
          query: getPublishedOutput,
          variables: {
            clientCode: activeCampaign?.ClientCode,
            clientCountry: activeCampaign?.ClientCountry,
            campaign_id: activeCampaign?.campaignId,
            dashboardType:
              DashboardTypes[
                dashType === 'TV' && videoSource === 'BVOD' ? 'BVOD' : (dashType as keyof typeof DashboardTypes)
              ],
            subCampaignId:
              dashType === 'TV' && videoSource === 'BVOD' ? '-1' : activeSubCampaign?.subcampaignId?.toString(),
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetPublishedOutputQuery };
        const placeholdersData = response.data.getPublishedOutput?.placeholders;
        console.log('PUBLISHEDDATAFETCHED', placeholdersData);

        if (!cancelled && placeholdersData) {
          setPublishedData(placeholdersData as Placeholder[]);
        }
      } catch (e) {
        console.error(e);
      }
    };
    // this call should be only if activecampaign.authorized = false

    const shouldThisTrygFetchPublishedData =
      !userCanDoInsource || (userCanDoInsource && dashType !== DashboardTypes.INSOURCE_DATA);

    if (dashType !== null) {
      if (
        activeCampaign?.authorized === false &&
        shouldThisTrygFetchPublishedData &&
        checkDashPublished(
          activeSubCampaignObject,
          dashType === 'TV' && videoSource === 'BVOD' ? DashboardTypes.BVOD : dashType,
        )
      ) {
        fetch();
      } else if (
        !activeCampaign?.authorized &&
        dashType === 'FLOWCHART' &&
        activeCampaign?.status === 'CAMPAIGN_PUBLISHED'
      ) {
        fetch();
      } else {
        if (!cancelled) setPublishedData([]);
      }
    }

    return () => { cancelled = true; };
  }, [
    activeCampaign?.ClientCode,
    activeCampaign?.ClientCountry,
    activeCampaign?.authorized,
    activeCampaign?.campaignId,
    activeSubCampaign?.subcampaignId,
    activeSubCampaignObject,
    dashType,
    videoSource,
  ]);

  const [
    campaignOverFlowdata,
    campaignOverFlowqueryStatus,
    campaignOverFlowerror,
    campaignOverFlowqueryId,
    campaignOverFlowoutputLocation,
  ] = useDataQuery({
    query: campaignOverflowKPIquery,
    querySource: 'CAMPAIGN OVERFLOW KPI' /* , publishedOutputLocation: publishedOutputLocation  */,
  });
  useEffect(() => {
    if (campaignOverFlowoutputLocation.length > 0) {
      const placeholderObject = {
        position: 'Summary',
        outputlocation: campaignOverFlowoutputLocation,
      };

      const KPIs = placeholders.SUMMARY;
      publishCampaign([placeholderObject, ...KPIs]);
      // publishCampaign(placeholderObject);
    }
  }, [campaignOverFlowoutputLocation, publishCampaign]);

  const handlePublishSummary = () => {
    if (activeCampaign && overflowKPIquery) {
      const newObject = structuredClone(overflowKPIquery);
      newObject.clients = [
        {
          country: countryEnum[activeCampaign.ClientCountry as keyof typeof countryEnum],
          client: activeCampaign.ClientCode,
        },
      ];
      newObject.query.filters = [
        {
          column: 'campaign_id',
          filter: {
            filtertype: filtertype.EQUAL,
            datatype: datatype.NUMBER,
            value: activeCampaign?.campaignId.toString(),
          },
        },
      ];
      setCampaignOverflowKPIquery(newObject);

      UpdateCampaign(activeCampaign, FlowchartCampaignStatus.CAMPAIGN_PUBLISHED);

      const campaign = permanentCampaignList?.find(
        (campaign: any) => campaign.campaignId === activeCampaign?.campaignId,
      );
      const newCampaign = {
        ...campaign,
        status: 'CAMPAIGN_PUBLISHED',
      } as ACdetails;

      const newCampaignList = permanentCampaignList?.map((campaign: ACdetails) => {
        if (campaign?.campaignId === activeCampaign?.campaignId) {
          return newCampaign;
        } else {
          return campaign;
        }
      }) as ACdetails[];

      setPermanentCampaignList(newCampaignList);
    }
  };

  return (
    <PublishContext.Provider
      value={{
        publishCampaign,
        updateOLArray,
        publishedData,
        unpublishCampaign,
        setPublishedData,
        isPublished,
        handlePublishSummary,
        campaignOverFlowqueryStatus,
        removePlaceholderfromPublishArray,
        placeholders,
        placeholdersStatus,
        setPlaceholdersStatus,
        placeholderResetToken,
      }}
    >
      {props.children}
    </PublishContext.Provider>
  );
};
